import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface CountdownProps {
  /** Date cible ISO. Vient de l'événement ou du réglage `countdown.target_at`. */
  target: string
  textBefore?: string
  textAfter?: string
}

interface Remaining {
  days: number
  hours: number
  minutes: number
  seconds: number
  finished: boolean
}

function computeRemaining(target: number): Remaining {
  const delta = target - Date.now()
  if (delta <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, finished: true }
  }
  const totalSeconds = Math.floor(delta / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    finished: false,
  }
}

/**
 * Compte à rebours calculé côté client à partir de la date configurée.
 *
 * Rien n'est figé : la cible, l'affichage et les textes qui l'entourent
 * viennent tous de la configuration de l'édition.
 */
export function Countdown({ target, textBefore, textAfter }: CountdownProps) {
  const { t } = useTranslation()
  const targetTime = useMemo(() => new Date(target).getTime(), [target])
  const [remaining, setRemaining] = useState(() => computeRemaining(targetTime))

  useEffect(() => {
    setRemaining(computeRemaining(targetTime))
    const timer = window.setInterval(() => setRemaining(computeRemaining(targetTime)), 1000)
    return () => window.clearInterval(timer)
  }, [targetTime])

  if (remaining.finished) {
    return (
      <p className="font-display text-heading-lg text-champagne-300">{t('countdown.live')}</p>
    )
  }

  const units = [
    { value: remaining.days, label: t('countdown.days') },
    { value: remaining.hours, label: t('countdown.hours') },
    { value: remaining.minutes, label: t('countdown.minutes') },
    { value: remaining.seconds, label: t('countdown.seconds') },
  ]

  return (
    <div>
      {textBefore ? <p className="overline mb-4 text-content-muted">{textBefore}</p> : null}

      <div
        className="flex items-start gap-5 sm:gap-8"
        // Un lecteur d'écran n'a que faire d'un décompte à la seconde : on lui
        // donne une phrase, et on masque la grille décorative.
        role="timer"
        aria-label={units.map((unit) => `${unit.value} ${unit.label}`).join(', ')}
      >
        {units.map((unit) => (
          <div key={unit.label} aria-hidden className="text-center">
            <div className="font-display text-4xl tabular-nums text-content-primary sm:text-6xl">
              {String(unit.value).padStart(2, '0')}
            </div>
            <div className="overline mt-2 text-content-muted">{unit.label}</div>
          </div>
        ))}
      </div>

      {textAfter ? <p className="mt-4 text-content-secondary">{textAfter}</p> : null}
    </div>
  )
}
